import { gql } from 'graphql-request';
import graphqlClient from './graphqlClient';

// ── Mutations ──

const UPLOAD_CSV = gql`
  mutation UploadCSV($file_base64: String!, $brand_id: String!, $original_filename: String!, $ultimate_parent_id: String) {
    uploadCSV(file_base64: $file_base64, brand_id: $brand_id, original_filename: $original_filename, ultimate_parent_id: $ultimate_parent_id) {
      upload_id
      file_path
      row_count
      columns_detected
      next_step
    }
  }
`;

const VALIDATE_CSV_MAPPING = gql`
  mutation ValidateCSVMapping($csv_upload_id: String!, $mappings: [CSVColumnMappingInput!]!) {
    validateCSVMapping(csv_upload_id: $csv_upload_id, mappings: $mappings) {
      valid
      errors
      mapped_count
      unmapped_csv_columns
      next_step
    }
  }
`;

const DEDUP_STORES = gql`
  mutation DedupStores($csv_upload_id: String!) {
    dedupStores(csv_upload_id: $csv_upload_id) {
      matched_count
      unmatched_count
      match_percentage
    }
  }
`;

const ENRICH_STORES = gql`
  mutation EnrichStoresWithData($csv_upload_id: String!) {
    enrichStoresWithData(csv_upload_id: $csv_upload_id) {
      updated_count
      predefined_enriched
      dynamic_enriched
      failed_count
      failures {
        poi_id
        field
        reason
      }
    }
  }
`;

const INSERT_FEEDBACK = gql`
  mutation InsertFeedback($csv_upload_id: uuid!, $user_id: String!, $rating: Int!, $feedback_text: String) {
    insert_app_import_feedback_one(object: {
      csv_upload_id: $csv_upload_id,
      user_id: $user_id,
      rating: $rating,
      feedback_text: $feedback_text
    }) {
      id
    }
  }
`;

// ── Queries ──

const GET_IMPORT_HISTORY = gql`
  query GetImportHistory($brand_id: String!) {
    app_csv_uploads(
      where: { brand_id: { _eq: $brand_id } }
      order_by: { upload_timestamp: desc }
    ) {
      id
      original_filename
      file_size_bytes
      upload_timestamp
      row_count
      matched_count
      unmatched_count
      status
      uploader_id
      uploader {
        id
        name
        email
      }
      feedback {
        id
        rating
        feedback_text
      }
    }
  }
`;

const GET_UNMATCHED_STORES = gql`
  query GetUnmatchedStores($csv_upload_id: uuid!) {
    app_unmatched_stores(
      where: { csv_upload_id: { _eq: $csv_upload_id } }
      order_by: { csv_row_number: asc }
    ) {
      id
      csv_row_number
      store_name
      store_number
      address
      raw_data
      manually_resolved
      resolved_to_poi_id
    }
  }
`;

const GET_UPLOAD_DEDUP_RESPONSE = gql`
  query GetUploadDedupResponse($id: uuid!) {
    app_csv_uploads_by_pk(id: $id) {
      dedup_api_response
    }
  }
`;

const GET_BRAND_NOTES = gql`
  query GetBrandNotes($brand_name: String!) {
    app_brand_notes(
      where: { brand_name: { _eq: $brand_name } }
      order_by: { created_at: desc }
    ) {
      id
      comment
      user_id
      created_at
      updated_at
    }
  }
`;

const INSERT_BRAND_NOTE = gql`
  mutation InsertBrandNote($brand_name: String!, $comment: String!, $user_id: String!) {
    insert_app_brand_notes_one(object: {
      brand_name: $brand_name,
      comment: $comment,
      user_id: $user_id
    }) {
      id
      comment
      user_id
      created_at
    }
  }
`;

// ── Service functions ──

export async function uploadCSV(
  fileBase64: string,
  brandId: string,
  originalFilename: string,
  ultimateParentId?: string,
) {
  const data: any = await graphqlClient.request(UPLOAD_CSV, {
    file_base64: fileBase64,
    brand_id: brandId,
    original_filename: originalFilename,
    ultimate_parent_id: ultimateParentId || null,
  });
  return data.uploadCSV;
}

export async function validateCSVMapping(
  csvUploadId: string,
  mappings: Array<{
    csv_column_name: string;
    mapped_to_field: string;
    field_type: string;
    data_type: string;
  }>,
) {
  const data: any = await graphqlClient.request(VALIDATE_CSV_MAPPING, {
    csv_upload_id: csvUploadId,
    mappings,
  });
  return data.validateCSVMapping;
}

export async function dedupStores(csvUploadId: string) {
  const data: any = await graphqlClient.request(DEDUP_STORES, {
    csv_upload_id: csvUploadId,
  });
  return data.dedupStores;
}

export async function enrichStores(csvUploadId: string) {
  const data: any = await graphqlClient.request(ENRICH_STORES, {
    csv_upload_id: csvUploadId,
  });
  return data.enrichStoresWithData;
}

export async function submitFeedback(
  csvUploadId: string,
  userId: string,
  rating: number,
  feedbackText?: string,
) {
  const data: any = await graphqlClient.request(INSERT_FEEDBACK, {
    csv_upload_id: csvUploadId,
    user_id: userId,
    rating,
    feedback_text: feedbackText || null,
  });
  return data.insert_app_import_feedback_one;
}

export async function getImportHistory(brandId: string) {
  const data: any = await graphqlClient.request(GET_IMPORT_HISTORY, {
    brand_id: brandId,
  });
  return data.app_csv_uploads || [];
}

export async function getUnmatchedStores(csvUploadId: string) {
  const data: any = await graphqlClient.request(GET_UNMATCHED_STORES, {
    csv_upload_id: csvUploadId,
  });
  return data.app_unmatched_stores || [];
}

export async function getUploadDedupResponse(uploadId: string) {
  const data: any = await graphqlClient.request(GET_UPLOAD_DEDUP_RESPONSE, {
    id: uploadId,
  });
  return data.app_csv_uploads_by_pk?.dedup_api_response || null;
}

export async function getBrandNotes(brandName: string) {
  const data: any = await graphqlClient.request(GET_BRAND_NOTES, {
    brand_name: brandName,
  });
  return data.app_brand_notes || [];
}

export async function addBrandNote(brandName: string, comment: string, userId: string) {
  const data: any = await graphqlClient.request(INSERT_BRAND_NOTE, {
    brand_name: brandName,
    comment,
    user_id: userId,
  });
  return data.insert_app_brand_notes_one;
}

/** Convert a File to base64 string (without the data:... prefix) */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the "data:...;base64," prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
